import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import BoundaryType from "@mapmycustomers/shared/enum/BoundaryType";
import Color from "@mapmycustomers/shared/enum/Color";
import BaseMapStyle, { isDarkMapStyle } from "@mapmycustomers/shared/enum/map/BaseMapStyle";
import { Territory } from "@mapmycustomers/shared/types/entity";
import AreaRegion from "@mapmycustomers/shared/types/territory/AreaRegion";
import AreaType from "@mapmycustomers/shared/types/territory/AreaType";
import { parseAreaType } from "@mapmycustomers/shared/util/territory/areaTypes";

import DataViewButton from "@app/component/DataView/DataViewButton";
import useMapView from "@app/component/view/utils/useMapView";
import AreaRegionFeature from "@app/scene/map/components/features/AreaRegionFeature";
import DemographyFeaturesLayer from "@app/scene/map/components/features/DemographyFeature/DemographyFeaturesLayer";
import AreaRegionList from "@app/scene/map/components/TerritoriesMode/components/AreaRegionList";
import CompoundRegion from "@app/scene/map/components/TerritoriesMode/components/CompoundRegion";
import messages from "@app/scene/map/components/TerritoriesMode/components/Sidebar/messages";
import generateShape from "@app/scene/map/components/TerritoriesMode/utils/generateShape";
import useAggregateDemographyData from "@app/scene/map/components/TerritoriesMode/utils/useAggregateDemographyData";
import useBackendCompoundPolygon from "@app/scene/map/components/TerritoriesMode/utils/useBackendCompoundPolygon";
import useBoundsContext from "@app/scene/map/components/TerritoriesMode/utils/useBoundsContext";
import useDynamicBoundaries from "@app/scene/map/components/TerritoriesMode/utils/useDynamicBoundaries";
import useFocusBbox from "@app/scene/map/components/TerritoriesMode/utils/useFocusBbox";
import useLookupData from "@app/scene/map/components/TerritoriesMode/utils/useLookupData";
import useRegionChangesListener from "@app/scene/map/components/TerritoriesMode/utils/useRegionChangesListener";
import useToggleHandler from "@app/scene/map/components/TerritoriesMode/utils/useToggleHandler";
import {
  areBoundariesVisible,
  getBoundaryType,
  getDemographicData,
} from "@app/scene/map/store/layers/boundaries/selectors";
import { RegionDemographicData } from "@app/scene/map/store/layers/boundaries/types/BoundariesLayerData";
import { getMapStyle } from "@app/scene/map/store/selectors";
import {
  countPinsInTerritory,
  enterTerritoryMode,
  setDataViewData,
  toggleDataViewVisibility,
} from "@app/scene/map/store/territoryMode/actions";
import {
  getTerritoryPinsInBoundsCount,
  isTerritoryDataViewLoading,
} from "@app/scene/map/store/territoryMode/selectors";
import { RootState } from "@app/store/rootReducer";
import { isCreating } from "@app/store/territories";
import { createTerritory } from "@app/store/territories/actions";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import useAnalytics from "@app/util/contexts/useAnalytics";
import useUndoableListState from "@app/util/hook/useUndoableListState";
import { MAP_ENTITY_TYPES } from "@app/util/map/consts";

import BaseCreateTerritorySidebar from "../BaseCreateTerritorySidebar";
// import AnyColor from "@app/types/AnyColor";
// import { GeoMultiPolygon } from "@app/types/shapes";
// import myCompoundPolygon, { bboxArea, pathArea } from "./test-polygon";
// import bbox from "@turf/bbox";
// import { lineString } from "@turf/helpers";

interface Props {
  areaType: AreaType;
  creating?: boolean;
  dataViewLoading: boolean;
  demographicData?: RegionDemographicData;
  demographyVisible: boolean;
  mapStyle: BaseMapStyle;
  onCreateTerritory?: typeof createTerritory.request;
  onEditTerritory: typeof enterTerritoryMode;
  onLoadPinCount: typeof countPinsInTerritory.request;
  onToggleDataViewVisibility: typeof toggleDataViewVisibility;
  pinsCount?: number;
  setDataViewData: typeof setDataViewData;
}

const SmartTerritorySidebar: React.FC<Props> = ({
  areaType,
  creating,
  dataViewLoading,
  demographicData,
  demographyVisible,
  mapStyle,
  onCreateTerritory,
  onEditTerritory,
  onLoadPinCount,
  onToggleDataViewVisibility,
  pinsCount,
  setDataViewData,
}) => {
  const { viewState } = useMapView();
  const intl = useIntl();
  const analytics = useExtendedAnalytics(["Create", "Smart"], useAnalytics());

  const { countryCode, level } = parseAreaType(areaType)!;
  useFocusBbox(countryCode);
  const [lookupData, lookupLoading] = useLookupData(countryCode);

  const [selectedRegionIds, setSelectedRegionIds, editorState, { redo, reset, undo }] =
    useUndoableListState<AreaRegion["id"]>([]);
  const [currentLevel, currentRegions, selectedCurrentRegionIds, fullySelectedCurrentRegionIds] =
    useDynamicBoundaries(areaType, lookupData, selectedRegionIds);

  const toggleRegionSelection = useToggleHandler(
    level,
    currentLevel,
    lookupData,
    setSelectedRegionIds
  );

  useEffect(() => {
    setDataViewData({ areaType, regionIds: selectedRegionIds });
  }, [areaType, selectedRegionIds, setDataViewData]);

  const [compoundPolygon, loadingCompoundPolygon, loadedRegionIds] = useBackendCompoundPolygon(
    areaType,
    selectedRegionIds,
    currentLevel,
    currentRegions
  );

  const [addingRegionIds, removingRegionIds] = useRegionChangesListener(
    selectedRegionIds,
    loadedRegionIds,
    level,
    currentLevel,
    lookupData
  );

  const [{ color, name, opacity, userIds }, setTerritoryInfo] = useState({
    color: Color.BLACK,
    // these initial values are actually ignored as they will be immediately replaced by the
    // default ones from the BaseCreateTerritorySidebar on onTerritoryChange callback
    name: "",
    opacity: 0.5,
    userIds: [],
  });

  const handleTerritoryFormChange = useCallback((name, color, opacity, userIds) => {
    setTerritoryInfo({ color, name, opacity, userIds });
  }, []);

  const nameIsValid = name.trim().length > 0;
  const selectedRegionsAreValid = selectedRegionIds.length > 0;
  const canSave = nameIsValid && selectedRegionsAreValid;

  const [fetchingShape, setFetchingShape] = useState<boolean>(false);

  const handleSave = useCallback(async () => {
    if (!canSave) {
      return;
    }
    setFetchingShape(true);
    // we have to generate shape from scratch instead of taking compoundPolygon shape because
    // compound shape might be only partially visible on screen, hence only a part of a shape
    // would be saved
    let shape;
    try {
      shape = await generateShape(areaType, selectedRegionIds);
    } catch {
      // Do nothing, we can legitimately fail shape generation, e.g. if all regions were PO Boxes
    }
    setFetchingShape(false);
    const { level } = parseAreaType(areaType)!;
    onCreateTerritory?.({
      onSuccess: (territory: Territory) => {
        onEditTerritory({ territory, territoryId: territory.id });
        analytics.completed("", { sharingWithUsers: userIds.length > 0 });
      },
      shape,
      territory: {
        adm1: [],
        adm2: [],
        adm3: [],
        color,
        coordinates: [],
        [level]: selectedRegionIds,
        name,
        opacity,
        type: areaType,
      },
      userIdsToShareWith: userIds,
    });
  }, [
    analytics,
    areaType,
    canSave,
    color,
    name,
    onCreateTerritory,
    onEditTerritory,
    opacity,
    selectedRegionIds,
    userIds,
  ]);

  const handleClick = useCallback(
    (region: AreaRegion, shouldAdd?: boolean) => toggleRegionSelection(region.id, shouldAdd),
    [toggleRegionSelection]
  );

  const regionDemographyData = useAggregateDemographyData(
    areaType,
    selectedRegionIds,
    demographicData
  );

  useEffect(() => {
    onLoadPinCount({ areaType, regionIds: selectedRegionIds });
  }, [
    areaType,
    onLoadPinCount,
    selectedRegionIds,
    // all deps are correct here and visibleEntities is not a mistake - we really
    // need to recalculate pin count when entity visibilities change
    // And the magic with MAP_ENTITY_TYPES.map is needed to keep number of deps
    // constant even if visibleEntities length changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ...MAP_ENTITY_TYPES.map((entityType) => viewState.visibleEntities.includes(entityType)),
  ]);

  const handleStartOver = useCallback(() => {
    reset([]);
  }, [reset]);
  const { setCanRedo, setCanUndo, setListeners } = useBoundsContext();
  useEffect(() => {
    setListeners({
      onRedo: redo,
      onStartOver: handleStartOver,
      onUndo: undo,
    });
  }, [handleStartOver, setListeners, undo, redo]);
  useEffect(() => setCanUndo(editorState.canUndo), [editorState.canUndo, setCanUndo]);
  useEffect(() => setCanRedo(editorState.canRedo), [editorState.canRedo, setCanRedo]);

  // useEffect(() => {
  //   myCompoundPolygon.coordinates[0].forEach((path, index) => {
  //     const a = pathArea(path);
  //     const box = bbox(lineString(path));
  //     const w = box[2] - box[0];
  //     const h = box[3] - box[1];
  //     const boxArea = bboxArea(box);
  //
  //     console.log(
  //       `path ${index} areas:`,
  //       a,
  //       boxArea,
  //       "area ratios=",
  //       boxArea / a,
  //       "aspect ratios=",
  //       w / h,
  //       h / w,
  //       Math.abs(w / h - h / w)
  //     );
  //   });
  // }, []);

  return (
    <AnalyticsContext.Provider value={analytics}>
      <BaseCreateTerritorySidebar
        count={pinsCount}
        creating={creating || fetchingShape}
        loading={loadingCompoundPolygon}
        onSave={handleSave}
        onTerritoryChanged={handleTerritoryFormChange}
        regionDemographyData={regionDemographyData}
        regionsSelected={selectedRegionIds.length}
        saveDisabled={
          (!nameIsValid
            ? intl.formatMessage(messages.emptyName)
            : !selectedRegionsAreValid
            ? intl.formatMessage(messages.emptyArea)
            : false) || creating
        }
        shouldShowPrompt={!creating && selectedRegionIds.length > 0}
      >
        <AreaRegionList
          action={
            <DataViewButton
              disableTooltip={!!regionDemographyData}
              entityCount={pinsCount ?? 0}
              loading={dataViewLoading}
              onClick={onToggleDataViewVisibility}
            />
          }
          areaType={areaType}
          loading={lookupLoading}
          lookupData={lookupData}
          onSelectionChange={setSelectedRegionIds}
          selectedRegionIds={selectedRegionIds}
        />
        {demographyVisible && (
          <DemographyFeaturesLayer countryCode={countryCode} disableLabel level={currentLevel} />
        )}
        <AreaRegionFeature
          addingRegionIds={addingRegionIds}
          areaType={areaType}
          baseMapIsDark={isDarkMapStyle(mapStyle)}
          currentLevel={currentLevel}
          fullySelectedCurrentRegionIds={fullySelectedCurrentRegionIds}
          hoverColor={color}
          lookupData={lookupData}
          onClick={handleClick}
          regions={currentRegions}
          removingRegionIds={removingRegionIds}
          selectedCurrentRegionIds={selectedCurrentRegionIds}
          selectedRegionIds={selectedRegionIds}
        />
        <CompoundRegion color={color} opacity={opacity} polygon={compoundPolygon} />
        {/*<CompoundRegion*/}
        {/*  color={"#ff0000" as AnyColor}*/}
        {/*  opacity={0.25}*/}
        {/*  polygon={myCompoundPolygon as GeoMultiPolygon}*/}
        {/*/>*/}
      </BaseCreateTerritorySidebar>
    </AnalyticsContext.Provider>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    creating: isCreating(state),
    dataViewLoading: isTerritoryDataViewLoading(state),
    demographicData: getDemographicData(state),
    demographyVisible:
      areBoundariesVisible(state) && getBoundaryType(state) === BoundaryType.DEMOGRAPHY,
    mapStyle: getMapStyle(state),
    pinsCount: getTerritoryPinsInBoundsCount(state),
  };
};

const mapDispatchToProps = {
  onCreateTerritory: createTerritory.request,
  onEditTerritory: enterTerritoryMode,
  onLoadPinCount: countPinsInTerritory.request,
  onToggleDataViewVisibility: toggleDataViewVisibility,
  setDataViewData,
};

export default connect(mapStateToProps, mapDispatchToProps)(SmartTerritorySidebar);
